import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Grid,
  CircularProgress,
  Button,
  Pagination,
  PaginationItem,
  Chip,
} from '@mui/material';
import { galleryService } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { AccessTime, Folder, VideoLibrary } from '@mui/icons-material';
import { getImageUrl, ASSETS } from '../utils/assets';
import { BRAND } from '../theme/pageStyles';
import SimplePageBanner from '../components/SimplePageBanner';
import SiteStatsItems from '../components/SiteStatsItems'; // Join With Us counters from admin

const BRAND_GREEN = '#477C2E';

const META_GREY = '#6b7280';
const CARD_THEMES = [
  { accent: '#dc2626', glow: 'rgba(220,38,38,0.25)' },
  { accent: BRAND_GREEN, glow: 'rgba(71,124,46,0.25)' },
  { accent: '#eab308', glow: 'rgba(234,179,8,0.25)' },
];

function formatGalleryDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

const PER_PAGE = 6;

const GalleryPage = () => {
  const navigate = useNavigate();
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await galleryService.getGallery();
        const data = response.data?.data || response.data || [];
        setGallery(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching gallery:', error);
        setGallery([]);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const totalPages = Math.max(1, Math.ceil(gallery.length / PER_PAGE));
  const paginatedGallery = gallery.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: BRAND.green }} />
      </Box>
    );
  }

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <SimplePageBanner
        subtitle="Moments of impact"
        title="Gallery"
        description="Explore photos and videos from our programs, events, and community work."
      />

      {/* Gallery Grid - redesigned media showcase (distinct from Campaigns) */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg" sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 4 }}>
            <Box>
              <Typography sx={{ fontFamily: '"Caveat", cursive', fontWeight: 700, fontSize: { xs: 24, md: 27 }, color: '#313530', mb: 0.5 }}>
                Our Gallery
              </Typography>
              <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: 14, color: META_GREY }}>
                Explore moments of impact through photos and videos.
              </Typography>
            </Box>
            <Chip
              label={`${gallery.length} Items`}
              sx={{
                backgroundColor: '#F3F7ED',
                color: BRAND_GREEN,
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 600,
                borderRadius: 999,
              }}
            />
          </Box>

          {paginatedGallery.length === 0 ? (
            <Typography sx={{ fontFamily: '"Poppins", sans-serif', textAlign: 'center', color: '#4B4646', py: 6 }}>
              No gallery items available
            </Typography>
          ) : (
            <>
              <Grid container spacing={3}>
                {paginatedGallery.map((item, index) => {
                  const columnIndex = index % 3;
                  const theme = CARD_THEMES[columnIndex];
                  const firstMedia = item.media && item.media.length > 0 ? item.media[0] : null;
                  const isVideo = firstMedia?.media_type === 'video';
                  const mediaUrl = firstMedia?.media_url;
                  const dateStr = formatGalleryDate(item.created_at || item.date) || 'Jan 18, 2013';
                  const category = item.category || 'Gallery';
                  const desc = item.description ? item.description.replace(/\s+/g, ' ').trim() : 'Our events, achievements, and community impact in pictures and stories.';
                  const descShort = desc.length > 95 ? desc.substring(0, 95) + '...' : desc;

                  return (
                    <Grid item xs={12} md={6} key={item.id}>
                      <Card
                        elevation={0}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          borderRadius: 3,
                          overflow: 'hidden',
                          boxShadow: '0 0 17px 0 rgba(0,0,0,0.06)',
                          cursor: 'pointer',
                          border: '1px solid rgba(0,0,0,0.04)',
                          transition: 'all 0.25s ease',
                          '&:hover': {
                            boxShadow: `0 16px 32px ${theme.glow}`,
                            transform: 'translateY(-4px)',
                          },
                        }}
                        onClick={() => navigate(`/gallery/${item.id}`)}
                      >
                        <Box sx={{ position: 'relative', width: { xs: '100%', sm: 210 }, minWidth: { sm: 210 }, height: { xs: 220, sm: '100%' }, backgroundColor: '#151515', overflow: 'hidden' }}>
                          {isVideo ? (
                            <Box sx={{ width: '100%', height: '100%', minHeight: { sm: 210 }, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1, color: 'white', background: 'linear-gradient(135deg,#1f2937,#111827)' }}>
                              <VideoLibrary sx={{ fontSize: 52, opacity: 0.9 }} />
                              <Typography variant="body2" sx={{ fontFamily: '"Poppins", sans-serif' }}>Video Preview</Typography>
                            </Box>
                          ) : (
                            <Box
                              component="img"
                              src={getImageUrl(mediaUrl) || 'https://via.placeholder.com/400x240?text=Gallery'}
                              alt={item.title}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                          )}
                          <Box sx={{ position: 'absolute', inset: 0, background: { xs: 'linear-gradient(to top, rgba(0,0,0,0.58) 0%, rgba(0,0,0,0.05) 55%)', sm: 'linear-gradient(to right, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.06) 55%)' } }} />
                          <Box sx={{ position: 'absolute', top: 14, left: 14 }}>
                            <Chip
                              size="small"
                              icon={isVideo ? <VideoLibrary sx={{ color: '#fff !important' }} /> : <Folder sx={{ color: '#fff !important' }} />}
                              label={isVideo ? 'Video' : category}
                              sx={{ backgroundColor: theme.accent, color: '#fff', fontFamily: '"Poppins", sans-serif', fontWeight: 600 }}
                            />
                          </Box>
                          <Typography sx={{ position: 'absolute', right: 12, bottom: 12, color: '#fff', fontFamily: '"Poppins", sans-serif', fontSize: 11, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 0.5, backgroundColor: 'rgba(0,0,0,0.35)', px: 0.75, py: 0.25, borderRadius: 1 }}>
                            <AccessTime sx={{ fontSize: 14 }} />
                            {dateStr}
                          </Typography>
                        </Box>

                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2.25, textAlign: 'left' }}>
                          <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: 12, fontWeight: 600, color: theme.accent, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {category}
                          </Typography>
                          <Typography
                            component="h3"
                            sx={{
                              fontFamily: '"Nunito", sans-serif',
                              fontWeight: 900,
                              fontSize: 20,
                              color: '#2D2929',
                              mb: 1,
                              lineHeight: 1.25,
                            }}
                          >
                            {item.title}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: '"Poppins", sans-serif',
                              fontWeight: 400,
                              fontSize: 13,
                              color: META_GREY,
                              lineHeight: 1.65,
                              mb: 1.75,
                              flexGrow: 1,
                            }}
                          >
                            {descShort}
                          </Typography>
                          <Box sx={{ width: 36, height: 3, borderRadius: 999, backgroundColor: theme.accent, mb: 1.2, opacity: 0.85 }} />
                          <Button
                            variant="text"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/gallery/${item.id}`);
                            }}
                            sx={{
                              alignSelf: 'flex-start',
                              borderRadius: 9999,
                              backgroundColor: 'transparent',
                              color: theme.accent,
                              fontFamily: '"Poppins", sans-serif',
                              fontWeight: 700,
                              fontSize: 12,
                              px: 0,
                              py: 0.25,
                              textTransform: 'uppercase',
                              letterSpacing: '0.03em',
                              '&:hover': { backgroundColor: 'transparent', opacity: 0.9, color: theme.accent },
                            }}
                          >
                            Explore Media →
                          </Button>
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                    renderItem={(item) => (
                      <PaginationItem
                        {...item}
                        sx={{
                          fontFamily: '"Poppins", sans-serif',
                          fontWeight: 600,
                          '&.Mui-selected': {
                            backgroundColor: '#dc2626',
                            color: '#fff',
                            '&:hover': { backgroundColor: '#b91c1c' },
                          },
                        }}
                      />
                    )}
                    sx={{
                      '& .MuiPagination-ul': { gap: 0.5 },
                      '& .MuiPaginationItem-root': { borderRadius: 2 },
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Container>
      </Box>

      {/* Join With Us - same as CampaignsPage */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 8 } }}>
        <Container maxWidth={false} sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
          <Typography
            sx={{
              fontFamily: '"Caveat", cursive',
              fontWeight: 700,
              fontSize: { xs: 24, md: 27 },
              lineHeight: '100%',
              letterSpacing: 0,
              color: '#313530',
              textAlign: 'center',
              mb: '25px',
            }}
          >
            Join With Us
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontFamily: '"Nunito", sans-serif',
              fontWeight: 900,
              fontSize: { xs: 26, sm: 34, md: 40 },
              lineHeight: '1.25em',
              letterSpacing: '0.01em',
              color: '#2D2929',
              textAlign: 'center',
              mb: { xs: '30px', md: '50px' },
            }}
          >
            Join in Roots & Wings
            <br />
            for Better Tomorrow
          </Typography>
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              maxWidth: 1200,
              mx: 'auto',
              borderRadius: { xs: 16, md: '23px' },
              minHeight: { xs: 200, md: 238 },
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid #c4a574',
              backgroundImage: `url(${ASSETS.parallax})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(36, 88, 78, 0.63)',
                borderRadius: '23px',
              },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                flexWrap: 'wrap',
                justifyContent: 'space-evenly',
                alignItems: 'center',
                flex: 1,
                minHeight: { xs: 200, md: 238 },
                gap: { xs: 3, sm: 0 },
                py: { xs: 4, md: 5 },
                px: { xs: 3, md: 4 },
              }}
            >
              <SiteStatsItems />
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default GalleryPage;
